import React, { useState } from "react";
import DatePickerInput from "../DatePickerInput";
import {
  DEPOSIT_METHOD_OPTIONS,
  FREQUENCY_OPTIONS,
} from "../../../../constants/accountConstants";
import { getHtmlDateMinMax } from "../../../../utils/dateFieldConstraints";

const SKIP_KEYS = new Set(["id", "status"]);

const isMissing = (val) => val === null || val === undefined || val === "";

const DepositsForm = ({ householdData, fieldData, missingFields, entityId, onSave, onSaveAndExit, saving, nextLabel }) => {
  const account = fieldData || {};
  const existingDeposits = account.deposits || [];

  const [formData, setFormData] = useState(() =>
    existingDeposits.map((d) => {
      const copy = {};
      Object.keys(d).forEach((k) => {
        if (!SKIP_KEYS.has(k)) copy[k] = d[k] ?? "";
      });
      return copy;
    })
  );

  const handleFieldChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const REQUIRED_FIELDS = ["amount", "frequency", "start_date"];
  const scheduleBounds = getHtmlDateMinMax("schedule_future");
  const totalMissing = existingDeposits.reduce(
    (sum, d) => sum + REQUIRED_FIELDS.filter((k) => isMissing(d[k])).length, 0
  );

  const buildPayload = () => {
    const update = {};
    formData.forEach((d, i) => {
      const orig = existingDeposits[i] || {};
      if (!orig.id) return;
      update[orig.id] = {
        amount: d.amount !== "" ? parseFloat(d.amount) : null,
        frequency: d.frequency || null,
        start_date: d.start_date || null,
        method: d.method || null,
      };
    });
    return { accounts: { [entityId]: { deposits: { update } } } };
  };

  const handleSubmit = (cb) => cb(buildPayload());

  if (existingDeposits.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">No deposit records to complete.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        {totalMissing > 0 && (
          <span className="text-sm text-red-500 font-medium">
            ● {totalMissing} Required field{totalMissing !== 1 ? "s" : ""} missing
          </span>
        )}
      </div>

      {existingDeposits.map((d, i) => {
        const data = formData[i] || {};
        return (
          <div key={d.id || i} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="bg-emerald-100 text-emerald-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">{i + 1}</span>
                Deposit Details
              </h3>
              {REQUIRED_FIELDS.some((k) => isMissing(d[k])) && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-wider border border-red-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                  Details Required
                </span>
              )}
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Amount ($) {isMissing(d.amount) && <span className="text-red-500">*</span>}
                  </label>
                  <input type="number" min={0} value={data.amount ?? ""} onChange={(e) => handleFieldChange(i, "amount", e.target.value)} placeholder="0.00" className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Frequency {isMissing(d.frequency) && <span className="text-red-500">*</span>}
                  </label>
                  <select value={data.frequency || ""} onChange={(e) => handleFieldChange(i, "frequency", e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                    <option value="">Select Frequency</option>
                  {FREQUENCY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Start Date {isMissing(d.start_date) && <span className="text-red-500">*</span>}
                  </label>
                  <DatePickerInput value={data.start_date || ""} onChange={(e) => handleFieldChange(i, "start_date", e.target.value)} min={scheduleBounds.min} max={scheduleBounds.max} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Method
                  </label>
                  <select value={data.method || ""} onChange={(e) => handleFieldChange(i, "method", e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                    <option value="">Select Method</option>
                    {DEPOSIT_METHOD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">Save & Exit</button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default DepositsForm;
